import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Collapse,
  Divider,
  useMediaQuery,
  useTheme,
  Tooltip,
  Popover,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import InfiniteScroll from "react-infinite-scroll-component";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getIssueMTCList,
  deleteIssueMTC,
  getIssueMTCDetail,
} from "../../../Services/Services";
import PrintIcon from "@mui/icons-material/Print";
import generateIssueMTCPdf from "./IssueMtcPrint";

const IssueMTCList = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [expandedItem, setExpandedItem] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // Search Filters
  const [searchFilter, setSearchFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");

  const [anchorEl, setAnchorEl] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const isInitialMount = useRef(true);

  // Get division from localStorage
  const getSelectedDivision = () => {
    try {
      const stored = localStorage.getItem("selectedDivision");
      return stored ? JSON.parse(stored) : null;
    } catch (e) {
      console.error("Failed to parse selectedDivision:", e);
      return null;
    }
  };

  const storedDivision = getSelectedDivision();

  const truncateText = (text, limit = 20) => {
    if (!text) return "";
    return text.length > limit ? text.substring(0, limit) + "..." : text;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  // Initial load
  useEffect(() => {
    if (storedDivision?.division_id) {
      fetchData(1, true, "", "");
    }
  }, []);

  // Debounced search effect
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (!storedDivision?.division_id) {
      setRows([]);
      return;
    }

    const handler = setTimeout(() => {
      setPage(1);
      fetchData(1, true, searchFilter, projectFilter);
    }, 500);

    return () => clearTimeout(handler);
  }, [searchFilter, projectFilter]);

  // Fetch Issue MTC Data
  const fetchData = async (
    pageNumber = 1,
    reset = false,
    search = "",
    project = "",
  ) => {
    try {
      if (!storedDivision?.division_id) {
        setRows([]);
        setHasMore(false);
        return;
      }
      setLoading(true);
      const data = await getIssueMTCList({
        page: pageNumber,
        search,
        division: storedDivision?.division_id,
        project,
      });

      const results = data.results || data.data || [];

      if (reset) {
        setRows(results);
      } else {
        setRows((prev) => [...prev, ...results]);
      }

      setHasMore(Boolean(data.next) || pageNumber < (data.total_pages || 1));
      setPage(pageNumber);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchMoreData = () => {
    if (hasMore && !loading) {
      fetchData(page + 1, false, searchFilter, projectFilter);
    }
  };

  // DELETE
  const handleDelete = async (id) => {
    try {
      const response = await deleteIssueMTC(id);
      if (response?.success) {
        setRows(rows.filter((item) => item.id !== id));
        toast.success(response.message || "Record deleted successfully!");
      } else {
        toast.error(response?.error || "Failed to delete record.");
      }
    } catch (error) {
      console.error("Error deleting record:", error);
      toast.error("Failed to delete record.");
    }
  };

  // EDIT - Navigate to form
  const handleEdit = (row) => {
    navigate("/dashboard/issue-mtc-form", {
      state: { data: row, mode: "edit" },
    });
  };

  // VIEW - Navigate to details or open in new tab
  const handleView = (row) => {
    navigate("/dashboard/issue-mtc-detail", {
      state: { data: row },
    });
  };

  // ADD - Navigate to form
  const handleAdd = () => {
    navigate("/dashboard/issue-mtc-form");
  };

  // Mobile Card Component
  const MobileCard = ({ row, index }) => {
    const isExpanded = expandedItem === row.id;

    return (
      <Box key={row.id}>
        <Box
          onClick={() =>
            setExpandedItem(expandedItem === row.id ? null : row.id)
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 1.5,
            cursor: "pointer",
            bgcolor: index % 2 === 0 ? "#ffffff" : "#f5f5f5",
            "&:active": { bgcolor: "#e3f2fd" },
            borderLeft: "4px solid #1976d2",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}
            >
              <Typography
                sx={{ fontSize: "0.7rem", color: "#666", minWidth: 25 }}
              >
                #{index + 1}
              </Typography>
              <Typography
                sx={{ fontWeight: "bold", fontSize: "0.85rem", flex: 1 }}
              >
                {row.item_name}
              </Typography>
            </Box>
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "#666",
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              <span style={{ fontSize: "0.7rem" }}>
                {row.division_name || "N/A"}
              </span>
            </Typography>
          </Box>
          <IconButton size="small" sx={{ ml: 1 }}>
            {isExpanded ? (
              <ExpandLess fontSize="small" />
            ) : (
              <ExpandMore fontSize="small" />
            )}
          </IconButton>
        </Box>

        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
          <Box
            sx={{
              px: 1.5,
              py: 1,
              bgcolor: index % 2 === 0 ? "#ffffff" : "#f5f5f5",
              borderLeft: "4px solid #1976d2",
            }}
          >
            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                DIVISION
              </Typography>
              <Typography
                sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
              >
                {row.division_name || "N/A"}
              </Typography>
            </Box>

            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                PROJECT
              </Typography>
              <Typography
                sx={{ fontSize: "0.7rem", color: "#555", lineHeight: 1.3 }}
              >
                {row.project_name || "N/A"}
              </Typography>
            </Box>

            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                ITEM NAME
              </Typography>
              <Typography
                sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
              >
                {row.item_name}
              </Typography>
            </Box>

            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                DRAWING NO
              </Typography>
              <Typography
                sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
              >
                {row.drawing_no}
              </Typography>
            </Box>

            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                IBR FORM NO
              </Typography>
              <Typography
                sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
              >
                {row.ibr_form_no}
              </Typography>
            </Box>

            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                TOTAL ITEMS
              </Typography>
              <Typography
                sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
              >
                {row.items_count || 0}
              </Typography>
            </Box>

            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                TOTAL QTY
              </Typography>
              <Typography
                sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
              >
                {row.total_qty || 0}
              </Typography>
            </Box>

            <Box sx={{ mb: 0.8 }}>
              <Typography
                sx={{
                  fontSize: "0.6rem",
                  color: "#999",
                  letterSpacing: "0.5px",
                  mb: 0.3,
                }}
              >
                CREATED ON
              </Typography>
              <Typography
                sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}
              >
                {formatDate(row.created_at)}
              </Typography>
            </Box>

            <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.6 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handlePrint(row);
                }}
                sx={{
                  bgcolor: "#e3f2fd",
                  borderRadius: "6px",
                  width: 32,
                  height: 32,
                  "&:hover": { bgcolor: "#bbdefb", transform: "scale(1.05)" },
                  transition: "all 0.2s",
                }}
              >
                <PrintIcon sx={{ fontSize: "1rem", color: "#1976d2" }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit(row);
                }}
                sx={{
                  bgcolor: "#e8f5e9",
                  borderRadius: "6px",
                  width: 32,
                  height: 32,
                  "&:hover": { bgcolor: "#c8e6c9", transform: "scale(1.05)" },
                  transition: "all 0.2s",
                }}
              >
                <EditIcon sx={{ fontSize: "1rem", color: "#2e7d32" }} />
              </IconButton>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setAnchorEl(e.currentTarget);
                  setItemToDelete(row.id);
                }}
                sx={{
                  bgcolor: "#ffebee",
                  borderRadius: "6px",
                  width: 32,
                  height: 32,
                  "&:hover": { bgcolor: "#ffcdd2", transform: "scale(1.05)" },
                  transition: "all 0.2s",
                }}
              >
                <DeleteIcon sx={{ fontSize: "1rem", color: "#f44336" }} />
              </IconButton>
            </Box>
          </Box>
        </Collapse>

        <Divider />
      </Box>
    );
  };

  const handlePrint = async (row) => {
    try {
      const response = await getIssueMTCDetail(row.id);
      const fullData = response.data;
      // 🔍 DEBUG — Check what you're actually getting
      console.log("fullData:", fullData.data);
      console.log("division_name:", fullData.division_name);
      console.log("items:", fullData.items);

      await generateIssueMTCPdf(fullData);
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <Box sx={{ mt: 0 }}>
      {/* Search Fields and Add Button */}
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          gap: 1,
          mt: -3,
          pt: -2,
          mb: 1,
        }}
      >
        {/* Search Fields Container */}
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 1,
            flex: 1,
          }}
        >
          <TextField
            label="Search"
            variant="outlined"
            value={searchFilter}
            onChange={(e) => setSearchFilter(e.target.value)}
            placeholder="Item, Drawing No, IBR Form No"
            disabled={!storedDivision?.division_id}
            fullWidth
            sx={{
              "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
              "& .MuiInputLabel-root": { fontSize: "0.7rem", top: -3 },
            }}
          />

          {/* <TextField
            label="Division"
            variant="outlined"
            value={storedDivision?.division || "No Division Selected"}
            disabled
            fullWidth
            sx={{
              "& .MuiInputBase-root": {
                height: 30,
                borderRadius: 0,
                backgroundColor: "#f0f0f0",
              },
              "& .MuiInputLabel-root": { fontSize: "0.7rem", top: -3 },
              "& .MuiInputBase-input.Mui-disabled": {
                WebkitTextFillColor: storedDivision?.division_id
                  ? "#333"
                  : "#999",
              },
            }}
          /> */}

          <TextField
            label="Project"
            variant="outlined"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            disabled={!storedDivision?.division_id}
            fullWidth
            sx={{
              "& .MuiInputBase-root": { height: 30, borderRadius: 0 },
              "& .MuiInputLabel-root": { fontSize: "0.7rem", top: -3 },
            }}
          />
        </Box>

        {/* Add Button */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleAdd}
          disabled={loading}
          sx={{
            height: 30,
            minWidth: 150,
            display: "flex",
            alignItems: "center",
            fontSize: "0.7rem",
            px: 2,
            borderRadius: "0",
            mt: isMobile ? 1 : 0,
          }}
        >
          <AddIcon fontSize="small" sx={{ mr: 0.5 }} /> Add Issue MTC
        </Button>
      </Box>

      {/* Data Display */}
      <InfiniteScroll
        dataLength={rows.length}
        next={fetchMoreData}
        hasMore={hasMore}
        height={isMobile ? "70vh" : "80vh"}
        loader={
          <Typography sx={{ textAlign: "center", p: 2 }}>Loading...</Typography>
        }
        endMessage={
          <Typography sx={{ textAlign: "center", p: 2 }}>
            {rows.length === 0 ? "No records found!" : "No more records!"}
          </Typography>
        }
      >
        {isMobile ? (
          <Box sx={{ bgcolor: "background.paper", pb: 2 }}>
            {rows.map((row, index) => (
              <MobileCard key={row.id || index} row={row} index={index} />
            ))}
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            sx={{ maxHeight: "80vh", overflow: "auto" }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 1200,
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  backgroundColor: "#f5f5f5",
                  borderBottom: "1px solid #ccc",
                },
              }}
            >
              <TableHead>
                <TableRow
                  sx={{
                    backgroundColor: "#f5f5f5",
                    "& th": {
                      fontWeight: "bold",
                      color: "#333",
                      borderBottom: "1px solid #999",
                      whiteSpace: "nowrap",
                      backgroundColor: "#f5f5f5",
                      fontSize: "0.80rem",
                    },
                  }}
                >
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                    }}
                  >
                    S. No.
                  </TableCell>
                  <TableCell>Division</TableCell>
                  <TableCell>Project</TableCell>
                  <TableCell>Item Name</TableCell>
                  <TableCell>Drawing No</TableCell>
                  <TableCell>IBR Form No</TableCell>
                  <TableCell>Total Items</TableCell>
                  <TableCell>Total Qty</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Created By</TableCell>
                  <TableCell
                    align="center"
                    className="no-print"
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {rows.map((row, index) => (
                  <TableRow
                    hover
                    key={row.id}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff", // 🔹 Alternate row color
                      "& td": {
                        padding: "0px",
                        fontSize: "0.7rem",
                        fontSize: { xs: "0.75rem", sm: "0.8125rem" }, // responsive font
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        maxWidth: 120,
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "black" : "inherit",
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "white" : "inherit",
                      },
                      "&:hover": {
                        backgroundColor: "#e3f2fd !important", // hover color
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        zIndex: 1,
                        backgroundColor:
                          index % 2 === 0 ? "#fafafa" : "#ffffff",
                      }}
                    >
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.division_name || "N/A"} arrow>
                        <span>
                          {truncateText(row.division_name || "N/A", 20)}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.project_name || "N/A"} arrow>
                        <span>
                          {truncateText(row.project_name || "N/A", 20)}
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.item_name || ""} arrow>
                        <span>{truncateText(row.item_name, 25)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.drawing_no || ""} arrow>
                        <span>{truncateText(row.drawing_no, 15)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={row.ibr_form_no || ""} arrow>
                        <span>{truncateText(row.ibr_form_no, 15)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{row.items_count || 0}</TableCell>
                    <TableCell>{row.total_qty || 0}</TableCell>
                    <TableCell>{formatDate(row.created_at)}</TableCell>
                    <TableCell>{row.created_by_name || "N/A"}</TableCell>
                    <TableCell
                      align="center"
                      className="no-print"
                      sx={{
                        position: "sticky",
                        right: 0,
                        zIndex: 1,
                        minWidth: 150,
                        backgroundColor:
                          index % 2 === 0 ? "#fafafa" : "#ffffff",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          gap: 0.5,
                        }}
                      >
                        <IconButton
                          size="small"
                          color="info"
                          onClick={() => handlePrint(row)}
                          sx={{
                            bgcolor: "#e3f2fd",
                            "&:hover": { bgcolor: "#bbdefb" },
                          }}
                        >
                          <PrintIcon sx={{ fontSize: "0.9rem" }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEdit(row)}
                          sx={{
                            bgcolor: "#e8f5e9",
                            "&:hover": { bgcolor: "#c8e6c9" },
                          }}
                        >
                          <EditIcon sx={{ fontSize: "0.9rem" }} />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setItemToDelete(row.id);
                          }}
                          sx={{
                            bgcolor: "#ffebee",
                            "&:hover": { bgcolor: "#ffcdd2" },
                          }}
                        >
                          <DeleteIcon sx={{ fontSize: "0.9rem" }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>

      {/* Delete Confirmation Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">
            Are you sure you want to delete?
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mt: 1,
              gap: 1,
            }}
          >
            <Button
              color="error"
              size="small"
              variant="contained"
              onClick={() => {
                handleDelete(itemToDelete);
                setAnchorEl(null);
                setItemToDelete(null);
              }}
            >
              Yes
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setAnchorEl(null);
                setItemToDelete(null);
              }}
            >
              No
            </Button>
          </Box>
        </Box>
      </Popover>

      <ToastContainer position="top-right" autoClose={3000} />
    </Box>
  );
};

export default IssueMTCList;
